<script lang="ts">
	import AmountInput from '$lib/AmountInput.svelte';
	import ButtonSubmit from '$lib/ButtonSubmit.svelte';
	import Collapsible from '$lib/Collapsible.svelte';
	import { valueComparisonAdjective } from '$lib/utils';
	import { partition } from 'lodash-es';
	import { Binoculars, Calculator } from 'lucide-svelte';
	import * as ss from 'simple-statistics';
	import { defaults, superForm } from 'sveltekit-superforms';
	import { zod } from 'sveltekit-superforms/adapters';
	import { z } from 'zod';

	interface Props {
		currency: string;
		dividendYieldHistory: DividendYieldHistory;
		dividendRate: number;
		sectorAverageYield?: number | undefined;
	}

	let {
		currency,
		dividendYieldHistory,
		dividendRate,
		sectorAverageYield = undefined
	}: Props = $props();

	const schema = z.object({
		price: z.number({
			required_error: 'Price is required'
		})
	});

	const { form, errors, enhance, allErrors, isTainted, tainted, submitting, delayed } = superForm(
		defaults(
			{
				price: undefined
			},
			zod(schema)
		),
		{
			SPA: true,
			validators: zod(schema),
			dataType: 'json',
			resetForm: false,
			async onUpdate({ form }) {
				if (form.valid) {
					estimatedYield = dividendRate / form.data.price;
				}
			},
			async onUpdated() {
				if (resultsSection) resultsSection.scrollIntoView({ behavior: 'smooth' });
			}
		}
	);

	let estimatedYield: number | undefined = $state();
	let resultsSection: HTMLElement | undefined = $state();
	let betterYields: {
		label: string;
		value: number;
	}[] = $state([]);
	let worseYields: {
		label: string;
		value: number;
	}[] = $state([]);
	let meanYield = $derived(ss.mean(dividendYieldHistory.map(({ dividendYield }) => dividendYield)));
	let historicalHigh = $derived(
		ss.max(dividendYieldHistory.map(({ dividendYield }) => dividendYield))
	);
	let historicalLow = $derived(
		ss.min(dividendYieldHistory.map(({ dividendYield }) => dividendYield))
	);

	$effect(() => {
		if (estimatedYield) {
			let yields = [
				{
					label: 'Historical High',
					value: historicalHigh
				},
				{
					label: 'Historical Average',
					value: meanYield
				},
				{
					label: 'Historical Low',
					value: historicalLow
				}
			];
			if (sectorAverageYield) {
				yields.push({
					label: 'Sector Average',
					value: sectorAverageYield
				});
			}
			yields = yields.sort((a, b) => b.value - a.value);
			[betterYields, worseYields] = partition(yields, ({ value }) => value > estimatedYield!);
		}
	});
</script>

<div class="border-gray-light rounded-xl border-1 p-5">
	<Calculator class="text-gray-dark mx-auto mb-3 h-6 w-6" />
	<p class="h3 mb-8 text-center">Target Entry or Exit Price</p>
	<form use:enhance class="mx-auto max-w-sm">
		<AmountInput
			type="currency"
			{currency}
			fraction={4}
			bind:value={$form.price}
			errors={$errors.price}
			placeholder="Enter price"
			class="mb-5"
		/>
		<ButtonSubmit
			disabled={$allErrors.length > 0 || !isTainted()}
			class="w-full"
			submitting={$submitting}
			delayed={$delayed}>Calculate Yield</ButtonSubmit
		>
	</form>
	{#if estimatedYield && !isTainted($tainted)}
		<section bind:this={resultsSection} class="scroll-target section-mt">
			<div class="bg-yellow-light mb-1 rounded-xl p-5">
				<p class="mb-3">
					Based on your entry or exit share price of <span class="body-bold"
						>{$form.price.toLocaleString('default', {
							style: 'currency',
							currency: 'SGD',
							currencyDisplay: 'narrowSymbol',
							minimumFractionDigits: 0,
							maximumFractionDigits: 4
						})}</span
					>, you will get an estimated yield of
				</p>
				<p class="h1-serif">
					{estimatedYield.toLocaleString('default', {
						style: 'percent',
						minimumFractionDigits: 1,
						maximumFractionDigits: 1
					})}
				</p>
			</div>
			<div class="bg-yellow-light mb-1 rounded-xl p-5">
				<p class="card-bold mb-3">See comparison:</p>
				<ul class="timeline timeline-vertical card-regular text-gray-dark">
					{#if betterYields.length}
						<li class="timeline-start active">
							<div class="grid w-full gap-4">
								{#each betterYields as { label, value }}
									<div class="flex w-full justify-between">
										<div>{label}</div>
										<div>
											{value.toLocaleString('default', {
												style: 'percent',
												minimumFractionDigits: 1,
												maximumFractionDigits: 1
											})}
										</div>
									</div>
								{/each}
							</div>
						</li>
					{/if}
					<li class="timeline-middle active-focused">
						<div class="h3 flex w-full justify-between text-black">
							<div>Estimated Yield</div>
							<div>
								{estimatedYield.toLocaleString('default', {
									style: 'percent',
									minimumFractionDigits: 1,
									maximumFractionDigits: 1
								})}
							</div>
						</div>
					</li>
					{#if worseYields.length}
						<li class="timeline-end active">
							<div class="grid w-full gap-4">
								{#each worseYields as { label, value }}
									<div class="flex w-full justify-between">
										<div>{label}</div>
										<div>
											{value.toLocaleString('default', {
												style: 'percent',
												minimumFractionDigits: 1,
												maximumFractionDigits: 1
											})}
										</div>
									</div>
								{/each}
							</div>
						</li>
					{/if}
				</ul>
			</div>
			<div class="bg-yellow-light mb-5 rounded-xl p-5">
				<header class="mb-2 flex">
					<div class="flex h-6 w-6 items-center justify-center rounded-full bg-black">
						<Binoculars class="text-white-pure h-4 w-4" />
					</div>
					<p class="card-bold ml-3">Key Insight</p>
				</header>
				<p>
					The estimated yield is <span class="body-bold"
						>{valueComparisonAdjective(estimatedYield, meanYield)} the historical average yield of {meanYield.toLocaleString(
							'default',
							{
								style: 'percent',
								minimumFractionDigits: 1,
								maximumFractionDigits: 1
							}
						)}</span
					>
				</p>
				{#if sectorAverageYield}
					<p class="mt-3">
						The estimated yield is <span class="body-bold"
							>{valueComparisonAdjective(estimatedYield, sectorAverageYield)} the sector average yield
							of {sectorAverageYield.toLocaleString('default', {
								style: 'percent',
								maximumFractionDigits: 1
							})}</span
						>
					</p>
				{/if}
			</div>

			<Collapsible title="Read the disclaimer">
				<ul class="small-regular text-gray-dark ml-5 list-disc">
					<li>
						The amount shown is for illustration purposes only, and actual dividend earned may
						differ because of various factors.
					</li>
					<li>Past performance is not a guarantee of future returns.</li>
					<li>
						All investments carry risk, including the risk that the investor may lose all or part of
						his investment.
					</li>
				</ul>
			</Collapsible>
		</section>
	{/if}
</div>
